// ShareJet booking details: header, the read-only cards, passengers, revenue, invoices and the edit modals.
import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App as AntdApp, Button, Space, Spin, Tag, Tooltip, Typography } from 'antd';
import { ArrowLeftOutlined, DownloadOutlined, EditOutlined, WarningOutlined } from '@ant-design/icons';

import { downloadCalculation, getBooking, updateBooking } from '../../api/endpoints';
import { BOOKING_STATUS_COLORS, BOOKING_STATUS_LABELS, statusLabel } from '../../data/statuses';
import { extractError, type SubmitFn, type UpdateBody } from './bookingUpdate';
import { FlightDetailsModal } from './FlightDetailsModal';
import { ItineraryModal } from './ItineraryModal';
import { AircraftModal } from './AircraftModal';
import { FboModal } from './FboModal';
import { BookingAdhocTags } from './BookingAdhocTags';
import { FlightDetailsCard } from './FlightDetailsCard';
import { ItineraryCard } from './ItineraryCard';
import { AircraftDetailsCard } from './AircraftDetailsCard';
import { FboCard } from './FboCard';
import { PricingCard } from './PricingCard';
import { CANCELLED_LOCKED, PassengersSection } from './PassengersSection';
import { RevenueCard } from './RevenueCard';
import { withAbbr } from '../../data/airportTime';
import { BookingInvoicesCard } from './BookingInvoicesCard';

const { Title, Text } = Typography;

type EditTarget = 'flight' | 'itinerary' | 'aircraft' | 'fbo' | null;

export function BookingDetailPage() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  // SHAR-848 — back to the page and tab this was opened from. The list keeps
  // both in its URL and hands it over on the row click; a detail opened from a
  // link or a refresh has no such state and falls back to the bare list.
  const loc = useLocation();
  const backTo = (loc.state as { from?: string } | null)?.from ?? '/bookings';
  const { message } = AntdApp.useApp();
  const qc = useQueryClient();

  const detail = useQuery({
    queryKey: ['booking', id],
    queryFn: () => getBooking(id),
    enabled: !!id,
  });

  const [editing, setEditing] = useState<EditTarget>(null);

  const b = detail.data;

  const mut = useMutation({
    mutationFn: (body: UpdateBody) => updateBooking(id, body),
    onSuccess: (data) => {
      qc.setQueryData(['booking', id], data);
      qc.invalidateQueries({ queryKey: ['booking', id] });
      qc.invalidateQueries({ queryKey: ['bookings'] });
      qc.invalidateQueries({ queryKey: ['bookingCounts'] }); // SHAR-1105 — the list's counters too
    },
  });


  if (detail.isLoading || !b) {
    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => nav(backTo)}>
          Back to bookings
        </Button>
        <div style={{ textAlign: 'center', padding: 48 }}>
          {detail.isLoading ? <Spin /> : <Text type="secondary">Booking not found.</Text>}
        </div>
      </Space>
    );
  }

  const submit: SubmitFn = (body) => mut.mutateAsync(body);

  // SHAR-347 — ad-hoc labels are removable via a Popconfirm that PATCHes
  // dismissMgavOverdue / clearNewSeats (same pattern as the bookings list).
  const removeAdhoc = (body: UpdateBody, label: string) =>
    mut.mutate(body, {
      onSuccess: () => message.success(`Removed “${label}”`),
      onError: (e) => message.error(extractError(e)),
    });

  const adhocTags = <BookingAdhocTags b={b} mut={mut} removeAdhoc={removeAdhoc} />;

  // SHAR-591 (retest) — a cancelled booking is final, so the section editors are
  // disabled rather than left inviting an edit the server will refuse. The
  // tooltip says why, otherwise a greyed-out button just looks broken.
  const cancelled = b?.status === 'cancelled';
  const editBtn = (target: EditTarget) => (
    <Tooltip title={cancelled ? CANCELLED_LOCKED : ''}>
      <Button
        size="small"
        icon={<EditOutlined />}
        disabled={cancelled}
        onClick={() => setEditing(target)}
      >
        Edit
      </Button>
    </Tooltip>
  );

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space style={{ justifyContent: 'space-between', width: '100%' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => nav(backTo)}>
          Back to bookings
        </Button>
        {/* SHAR-320 — download the calculation snapshot .txt. */}
        <Button
          icon={<DownloadOutlined />}
          onClick={() =>
            downloadCalculation(b.id).catch((e) => message.error(extractError(e)))
          }
        >
          Calculation (.txt)
        </Button>
      </Space>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Booking <Text code>{b.id.slice(-8)}</Text>
          </Title>
          <Text type="secondary">
            {b.route.origin.code} → {b.route.dest.code} · {withAbbr(`${b.departureDate} ${b.departureTime}`, b.departureZoned)}
          </Text>
        </div>
        <Space>
          <Tag
            color={BOOKING_STATUS_COLORS[b.status]}
            style={{ fontSize: 14, padding: '2px 10px' }}
          >
            {statusLabel(BOOKING_STATUS_LABELS, b.status, b.statusLabel)}
          </Tag>
          {b.mgavOverdue ? (
            <Tag
              icon={<WarningOutlined />}
              color="volcano"
              style={{ fontSize: 14, padding: '2px 10px' }}
            >
              MGAV overdue
            </Tag>
          ) : null}
        </Space>
      </div>

      {/* 1. Flight details */}
      <FlightDetailsCard b={b} extra={editBtn('flight')} adhocTags={adhocTags} />

      {/* 2. Itinerary — SHAR-351: 3 columns (origin+departure | flight time | dest+arrival),
          styled with the same bordered Descriptions as Flight details. */}
      <ItineraryCard b={b} extra={editBtn('itinerary')} />

      {/* 3. Aircraft details — SHAR-353: 3 columns (operator | model+tail | category+seats),
          styled with the same bordered Descriptions as Flight details. */}
      <AircraftDetailsCard b={b} extra={editBtn('aircraft')} />

      {/* 4. FBO */}
      <FboCard b={b} extra={editBtn('fbo')} />

      {/* Pricing summary */}
      <PricingCard b={b} />

      {/* SHAR-48 — passengers grouped by user, with per-user CRUD. */}
      <PassengersSection booking={b} bookingId={id} />

      {/* SHAR-107 — revenue breakdown + per-user payments */}
      <RevenueCard b={b} />


      {/* Invoices */}
      <BookingInvoicesCard b={b} />

      {/* Edit modals */}
      <FlightDetailsModal
        open={editing === 'flight'}
        onClose={() => setEditing(null)}
        booking={b}
        onSubmit={submit}
        submitting={mut.isPending}
      />
      <ItineraryModal
        open={editing === 'itinerary'}
        onClose={() => setEditing(null)}
        booking={b}
        onSubmit={submit}
        submitting={mut.isPending}
      />
      <AircraftModal
        open={editing === 'aircraft'}
        onClose={() => setEditing(null)}
        booking={b}
        onSubmit={submit}
        submitting={mut.isPending}
      />
      <FboModal
        open={editing === 'fbo'}
        onClose={() => setEditing(null)}
        booking={b}
        onSubmit={submit}
        submitting={mut.isPending}
      />
    </Space>
  );
}
